/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';

import { Button, Dialog, Grid, InlineNotification, TextField, Typography2, breakpoints } from '@coursera/cds-core';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import BasicInformationQuery from 'bundles/account-settings/components/sections/basic-information/BasicInformationQuery.graphql';
import LanguageSelect from 'bundles/account-settings/components/sections/basic-information/LanguageSelect';
import TimezoneSelect from 'bundles/account-settings/components/sections/basic-information/TimezoneSelect';
import type { BasicInformationQueryQuery } from 'bundles/account-settings/components/sections/basic-information/__generated__/BasicInformationQuery';
import {
  getFieldErrorMessage,
  getPasswordErrorMessage,
  getSaveStatusMessage,
} from 'bundles/account-settings/components/sections/basic-information/statusMessages';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import type { ProfileSetting } from 'bundles/account-settings/constants/SettingsConstants';
import type { EnterpriseUserFeaturesContextType } from 'bundles/account-settings/contexts/EnterpriseUserFeaturesContext';
import { useEnterpriseUserFeatures } from 'bundles/account-settings/contexts/EnterpriseUserFeaturesContext';
import { useResyncingState } from 'bundles/account-settings/hooks/useResyncingState';
import { useUpdateBasicInformation } from 'bundles/account-settings/hooks/useUpdateBasicInformation';
import type { UpdateStatus } from 'bundles/account-settings/types/EmailPreferences';
import {
  USER_EMAIL_REGEX_PATTERN,
  USER_NAME_MAX_LENGTH,
  USER_NAME_MIN_LENGTH,
} from 'bundles/user-account/common/constants';
import { getResponseErrorCode } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

const { Fields, UpdateStates, Sections } = SettingsConstants;

type EmailChangeError = { responseJSON?: { message?: string } };

const EMAIL_REGEX = new RegExp(`^${USER_EMAIL_REGEX_PATTERN}$`);

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  headingGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  heading: css`
    && {
      margin: 0;
    }
  `,
  subtitle: css`
    && {
      margin: 0;
    }
  `,
  saveRow: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  liveRegion: css`
    width: 100%;
    min-height: 0;

    &:not(:empty) {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  dialogActions: css`
    display: flex;
    gap: var(--cds-spacing-100);
    justify-content: flex-start;
  `,
  validationBelowInput: css`
    display: flex;
    flex-direction: column;

    .cds-text-field-formValidationStatus {
      order: 1;
      margin-top: var(--cds-spacing-50);
    }
  `,
};

type OuterProps = {
  fullName: string | undefined;
  emailAddress: string | undefined;
  timezone: string;
  locale: string;
  updateStatus: UpdateStatus;
  invalidFields: string[];
  updatedEmailAddress: string;
  ssoOrgName: string | undefined;
};

type ProfileProps = {
  profile: {
    id: number;
  };
};

type InnerProps = ProfileProps &
  Pick<EnterpriseUserFeaturesContextType, 'isUserSsoLoginForced' | 'isUnifiedSso'> &
  OuterProps;

export const BasicInformation = ({
  fullName: fullNameProp,
  emailAddress: emailAddressProp,
  timezone: timezoneProp,
  locale: localeProp,
  updateStatus,
  invalidFields,
  updatedEmailAddress,
  ssoOrgName,
  profile,
  isUserSsoLoginForced,
  isUnifiedSso,
}: InnerProps) => {
  // useResyncingState (not useState) so these fields pick up their real value once the Flux
  // store finishes hydrating past its initial/default value shortly after mount, without
  // clobbering an in-progress edit the user has already made. See LO-5092.
  const [fullName, setFullName] = useResyncingState<string | undefined>(fullNameProp);
  const [emailAddress, setEmailAddress] = useResyncingState<string | undefined>(emailAddressProp);
  const [timezone, setTimezone] = useResyncingState<string>(timezoneProp);
  const [locale, setLocale] = useResyncingState<string>(localeProp);
  const [password, setPassword] = useState('');
  const [showEnterPasswordModal, setShowEnterPasswordModal] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [errorCode, setErrorCode] = useState('');

  const isEmailReadOnly = Boolean(isUserSsoLoginForced || isUnifiedSso);

  const isEmailDirty = !isEmailReadOnly && emailAddressProp !== emailAddress;
  const hasChanges = fullNameProp !== fullName || isEmailDirty || timezoneProp !== timezone || localeProp !== locale;

  const updateBasicInformation = useUpdateBasicInformation();

  const saveNonEmailSettings = useCallback(() => {
    const newSettings: ProfileSetting[] = [];

    if (fullNameProp !== fullName) {
      newSettings.push({ type: Fields.FULL_NAME, value: fullName });
    }

    // Blank timezone/locale always means "unset or not yet hydrated," never a learner choice --
    // neither select can emit "". Drop it rather than send it: learner-object rejects blank
    // values (LO-5088), and a rejection here fails the whole sequential save.
    if (timezoneProp !== timezone && (timezone ?? '').trim() !== '') {
      newSettings.push({ type: Fields.TIMEZONE, value: timezone });
    }

    if (localeProp !== locale && (locale ?? '').trim() !== '') {
      newSettings.push({ type: Fields.LOCALE, value: locale });
    }

    if (newSettings.length > 0) {
      updateBasicInformation(newSettings);
    }
  }, [fullName, fullNameProp, locale, localeProp, timezone, timezoneProp, updateBasicInformation]);

  const handleSubmitButton = useCallback(() => {
    SettingsActionCreators.resetSection(Sections.BASIC_INFORMATION);

    const trimmedFullName = (fullName ?? '').trim();
    const isFullNameInvalid =
      trimmedFullName.length < USER_NAME_MIN_LENGTH || trimmedFullName.length > USER_NAME_MAX_LENGTH;
    const isEmailInvalid = !emailAddress || !EMAIL_REGEX.test(emailAddress);

    if (isFullNameInvalid) {
      SettingsServerActionCreators.updateFieldFailed(Fields.FULL_NAME);
      // Defer so the TextField's error attributes commit before the screen reader reads the focused field.
      requestAnimationFrame(() => document.getElementById('settings-basic-full-name')?.focus());
      return;
    }
    if (isEmailInvalid && !isEmailReadOnly) {
      SettingsServerActionCreators.updateFieldFailed(Fields.EMAIL_ADDRESS);
      requestAnimationFrame(() => document.getElementById('settings-basic-email')?.focus());
      return;
    }

    if (emailAddressProp !== emailAddress) {
      setShowEnterPasswordModal(true);
      return;
    }

    saveNonEmailSettings();
  }, [emailAddress, emailAddressProp, fullName, isEmailReadOnly, saveNonEmailSettings]);

  const handleFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSubmitButton();
    },
    [handleSubmitButton]
  );

  const handleFullNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFullName(event.target.value);
      if (invalidFields.includes(Fields.FULL_NAME) || updateStatus.state !== UpdateStates.IDLE) {
        SettingsActionCreators.resetSection(Sections.BASIC_INFORMATION);
      }
    },
    [invalidFields, setFullName, updateStatus.state]
  );

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmailAddress(event.target.value);
      if (invalidFields.includes(Fields.EMAIL_ADDRESS) || updateStatus.state !== UpdateStates.IDLE) {
        SettingsActionCreators.resetSection(Sections.BASIC_INFORMATION);
      }
    },
    [invalidFields, setEmailAddress, updateStatus.state]
  );

  const handleChangeEmail = useCallback(() => {
    setIsInProgress(true);

    SettingsActionCreators.updateEmailAddressWithPassword(emailAddress || '', password, profile.id)
      .then(() => {
        setShowEnterPasswordModal(false);
        setErrorCode('');
        saveNonEmailSettings();
      })
      .catch((resp: EmailChangeError) => {
        setErrorCode(getResponseErrorCode(resp?.responseJSON?.message ?? ''));
      })
      .finally(() => {
        setIsInProgress(false);
      });
  }, [emailAddress, password, profile.id, saveNonEmailSettings]);

  const closeEnterPasswordModal = useCallback(() => {
    setShowEnterPasswordModal(false);
    setErrorCode('');
    setPassword('');
  }, []);

  const isUpdating = updateStatus.state === UpdateStates.UPDATING;
  const saveStatus = getSaveStatusMessage(updateStatus.state);
  const fullNameFieldHasError = invalidFields.includes(Fields.FULL_NAME);
  const emailFieldHasError = invalidFields.includes(Fields.EMAIL_ADDRESS);

  // Suppress the save notification while the password modal or its follow-up
  // verification dialog is in play so the email flow owns its own feedback.
  const isEmailFlowActive = showEnterPasswordModal || Boolean(updatedEmailAddress);
  const visibleSaveStatus = isEmailFlowActive ? null : saveStatus;

  useEffect(() => {
    if (errorCode) {
      requestAnimationFrame(() => document.getElementById('settings-basic-password')?.focus());
    }
  }, [errorCode]);

  const passwordErrorMessage = getPasswordErrorMessage(errorCode);
  const isPasswordEmpty = password.length === 0;

  return (
    <React.Fragment>
      <form onSubmit={handleFormSubmit} data-e2e="basic-information">
        <div css={styles.container}>
          <div css={styles.headingGroup}>
            <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
              {_t('Personal information')}
            </Typography2>
            <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.subtitle}>
              {_t('Update your personal details and how others see you.')}
            </Typography2>
          </div>

          {isUserSsoLoginForced && (
            <InlineNotification severity="information" title={_t('Your log-in method is Single sign-on')}>
              {ssoOrgName
                ? _t(
                    `Your email is managed by #{orgName}'s authentication provider and can't be changed directly in Coursera.`,
                    { orgName: ssoOrgName }
                  )
                : _t(
                    `Your email is managed by your organization's authentication provider and can't be changed directly in Coursera.`
                  )}
            </InlineNotification>
          )}

          <Grid container spacing={24}>
            <Grid item xs={12} md={6}>
              <TextField
                id="settings-basic-full-name"
                label={_t('Full name')}
                name="fullName"
                value={fullName ?? ''}
                onChange={handleFullNameChange}
                autoComplete="name"
                type="text"
                fullWidth
                optional
                necessityIndicator="none"
                validationStatus={fullNameFieldHasError ? 'error' : undefined}
                validationLabel={fullNameFieldHasError ? getFieldErrorMessage(Fields.FULL_NAME) : undefined}
                css={styles.validationBelowInput}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="settings-basic-email"
                label={_t('Email address')}
                name="emailAddress"
                value={emailAddress ?? ''}
                onChange={handleEmailChange}
                autoComplete="email"
                type="email"
                readOnly={isEmailReadOnly}
                fullWidth
                optional
                necessityIndicator="none"
                validationStatus={emailFieldHasError ? 'error' : undefined}
                validationLabel={emailFieldHasError ? getFieldErrorMessage(Fields.EMAIL_ADDRESS) : undefined}
                inputProps={{
                  'data-e2e': 'settings-basic-email',
                }}
                css={styles.validationBelowInput}
              />
            </Grid>
          </Grid>

          <Grid container spacing={24}>
            <Grid item xs={12} md={6}>
              <TimezoneSelect value={timezone} onChange={setTimezone} />
            </Grid>

            <Grid item xs={12} md={6}>
              <LanguageSelect
                value={locale}
                onChange={(event) => {
                  if (typeof event.target.value === 'string') setLocale(event.target.value);
                }}
              />
            </Grid>
          </Grid>

          <div css={styles.saveRow}>
            <div css={styles.liveRegion} role="alert" aria-live="assertive" aria-atomic="true">
              {visibleSaveStatus?.status === 'error' && (
                <InlineNotification severity="error">
                  {visibleSaveStatus.message ??
                    _t('There was an issue submitting your request. Please try again later.')}
                </InlineNotification>
              )}
            </div>
            <div
              css={styles.liveRegion}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="basic-info-success-status"
            >
              {visibleSaveStatus?.status === 'success' && (
                <InlineNotification severity="success">{visibleSaveStatus.message}</InlineNotification>
              )}
            </div>
            <Button
              variant="primary"
              disabled={!hasChanges}
              loading={isUpdating}
              type="submit"
              css={styles.button}
              data-testid="basic-info-save-button"
            >
              {_t('Save Changes')}
            </Button>
          </div>
        </div>
      </form>

      <Dialog open={showEnterPasswordModal} onClose={closeEnterPasswordModal}>
        <Dialog.HeadingGroup>
          {_t('To change your email address, please confirm your Coursera password.')}
        </Dialog.HeadingGroup>
        <Dialog.Content>
          <TextField
            id="settings-basic-password"
            type="password"
            label={_t('Password')}
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={isInProgress}
            fullWidth
            optional
            necessityIndicator="none"
            validationStatus={passwordErrorMessage ? 'error' : undefined}
            validationLabel={passwordErrorMessage}
          />
        </Dialog.Content>
        <Dialog.Actions css={styles.dialogActions}>
          <Button
            variant="primary"
            loading={isInProgress}
            disabled={isPasswordEmpty || isInProgress}
            onClick={handleChangeEmail}
            type="button"
            data-testid="update-email-action"
          >
            {isInProgress ? _t('Updating Email') : _t('Update Email')}
          </Button>
          <Button variant="ghost" onClick={closeEnterPasswordModal} type="button">
            {_t('Cancel')}
          </Button>
        </Dialog.Actions>
      </Dialog>

      <Dialog
        open={Boolean(updatedEmailAddress)}
        onClose={() => SettingsActionCreators.resetSection(Sections.BASIC_INFORMATION)}
        data-testid="verification-sent-dialog"
      >
        <Dialog.HeadingGroup>{_t('Verification sent')}</Dialog.HeadingGroup>
        <Dialog.Content>
          <Typography2 variant="bodyPrimary" component="p">
            {_t(
              `A verification email has been sent to #{email}. It may take several minutes to appear in your inbox.`,
              { email: updatedEmailAddress }
            )}
          </Typography2>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            variant="primary"
            onClick={() => SettingsActionCreators.resetSection(Sections.BASIC_INFORMATION)}
            type="button"
            data-testid="verification-close-button"
          >
            {_t('Close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </React.Fragment>
  );
};

const BasicInformationWrapper = (props: OuterProps) => {
  const { isUserSsoLoginForced, isUnifiedSso } = useEnterpriseUserFeatures();
  const { data, loading } = useQuery<BasicInformationQueryQuery>(BasicInformationQuery);

  const me = data?.User?.me;

  if (!data || loading || !me) {
    return null;
  }

  const profile = {
    id: Number(me.id),
  };

  return (
    <BasicInformation
      {...props}
      profile={profile}
      isUserSsoLoginForced={isUserSsoLoginForced}
      isUnifiedSso={isUnifiedSso}
    />
  );
};

export default BasicInformationWrapper;
